import React, { useState } from "react";
import axios from "axios";
import { useNavigate }    from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Capacitor }      from "@capacitor/core";
import {
    Box, Button, CircularProgress, IconButton,
    InputAdornment, Paper, TextField, Typography, Alert,
} from "@mui/material";
import EmailIcon         from "@mui/icons-material/Email";
import LockIcon          from "@mui/icons-material/Lock";
import VisibilityIcon    from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import { API_URL } from "../api/index";

export default function Register({ onRegisterSuccess }) {
    const navigate = useNavigate();
    const { t }    = useTranslation();

    const [email,       setEmail]       = useState("");
    const [password,    setPassword]    = useState("");
    const [confirm,     setConfirm]     = useState("");
    const [mostrarPass, setMostrarPass] = useState(false);
    const [error,       setError]       = useState("");
    const [loading,     setLoading]     = useState(false);

    const handleSubmit = async (e) => {
        e.preventDefault();
        setError("");
        if (password !== confirm) { setError(t("register.mismatch")); return; }

        setLoading(true);
        try {
            const res = await axios.post(
                `${API_URL}/auth/register`,
                { email, contraseña: password },
                { headers: { "Content-Type": "application/json" } }
            );
            localStorage.setItem("token",       res.data.access_token);
            localStorage.setItem("user_nombre", res.data.nombre || "");
            localStorage.setItem("user_foto",   res.data.foto   || "");
            localStorage.setItem("user_email",  email.toLowerCase());

            if (onRegisterSuccess) {
                onRegisterSuccess();
            } else if (Capacitor.isNativePlatform()) {
                navigate("/compare");
            } else {
                navigate("/dashboard");
            }
        } catch (err) {
            setError(t("register.error") + (err.response?.data?.detail || err.message));
        } finally { setLoading(false); }
    };

    return (
        <Box sx={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", bgcolor: "background.default", p: 2 }}>
            <Paper elevation={4} sx={{ width: "100%", maxWidth: 420, p: { xs: 3, sm: 4 }, borderRadius: 4 }}>
                <Typography variant="h5" sx={{ fontWeight: 800, mb: 3, textAlign: "center", color: "primary.main" }}>
                    {t("register.title")}
                </Typography>

                <Box component="form" onSubmit={handleSubmit} sx={{ display: "flex", flexDirection: "column", gap: 2 }}>
                    <TextField
                        type="email" label={t("register.email")} value={email}
                        onChange={(e) => setEmail(e.target.value)} required fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><EmailIcon color="action" fontSize="small" /></InputAdornment> }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    <TextField
                        type={mostrarPass ? "text" : "password"} label={t("register.password")}
                        value={password} onChange={(e) => setPassword(e.target.value)} required fullWidth
                        InputProps={{
                            startAdornment: <InputAdornment position="start"><LockIcon color="action" fontSize="small" /></InputAdornment>,
                            endAdornment: (
                                <InputAdornment position="end">
                                    <IconButton onClick={() => setMostrarPass(v => !v)} edge="end" size="small">
                                        {mostrarPass ? <VisibilityOffIcon fontSize="small" /> : <VisibilityIcon fontSize="small" />}
                                    </IconButton>
                                </InputAdornment>
                            ),
                        }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    <TextField
                        type={mostrarPass ? "text" : "password"} label={t("register.confirm")}
                        value={confirm} onChange={(e) => setConfirm(e.target.value)} required fullWidth
                        InputProps={{ startAdornment: <InputAdornment position="start"><LockIcon color="action" fontSize="small" /></InputAdornment> }}
                        sx={{ "& .MuiOutlinedInput-root": { borderRadius: 2 } }}
                    />

                    {error && <Alert severity="error" sx={{ borderRadius: 2 }}>{error}</Alert>}

                    <Button type="submit" variant="contained" fullWidth disabled={loading}
                            sx={{ borderRadius: 2, minHeight: 48, fontWeight: 700, textTransform: "none", mt: 1 }}>
                        {loading ? <CircularProgress size={22} color="inherit" /> : t("register.button")}
                    </Button>
                </Box>

                <Button fullWidth onClick={() => navigate("/login")}
                        sx={{ mt: 2, textTransform: "none", color: "primary.main", fontWeight: 600 }}>
                    {t("register.back")}
                </Button>
            </Paper>
        </Box>
    );
}